import { PMDataTable } from "../../../utils/PMTableView/PMDataTable";
import { CellLink } from "../../../utils/PMTableView/CellLink";
import {
  FullReportAddress,
  ReportStatus,
  ReportAppointmentApprovedStatus,
  reportStatusesOptions,
} from "./utils";

export const fmtDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("nb-NO", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

/** The agreement/appointment date lives on the nested `appointment` object. */
export const reportAppointmentDate = (report) =>
  report?.appointment?.date || null;

const statusLabel = (value) => {
  if (!value) return null;
  const match = reportStatusesOptions.find(
    (o) => String(o.value).toLowerCase() === String(value).toLowerCase()
  );
  return match ? match.label : value;
};

// Full column definitions (with cell renderers + grouping). Exported so the
// list page can derive column metadata for the toolbar / useTableView hook.
export const getReportColumns = (t) => [
  {
    key: "report_number",
    label: t("Report"),
    sortKey: "report_number",
    width: 150,
    always: true,
    cardRole: "title",
    cell: (report) => (
      <CellLink
        to={report?.id ? `/dashboard/service/reports/${report.id}` : null}
        className="text-[13.5px] font-semibold tabular-nums text-blue-600"
      >
        {report.report_number}
      </CellLink>
    ),
  },
  {
    key: "status",
    label: t("Status"),
    sortKey: "status",
    width: 170,
    stopRowClick: true,
    groupable: true,
    cardRole: "status",
    groupValue: (report) => report?.status || "",
    groupLabel: (key) => statusLabel(key) || t("table_view.no_value"),
    cell: (report) => (
      <div className="flex flex-col gap-1">
        <ReportStatus report={report} />
        <ReportAppointmentApprovedStatus report={report} type="short" />
      </div>
    ),
  },
  {
    key: "report_type",
    label: t("Type"),
    width: 150,
    cardRole: "meta",
    groupable: true,
    groupValue: (report) => report?.report_type?.title || "",
    groupLabel: (key) => key || t("table_view.no_value"),
    cell: (report) => (
      <span className="text-[13px] text-slate-500">
        {report?.report_type?.title || "—"}
      </span>
    ),
  },
  {
    key: "appointment",
    label: t("report.list.agreed_date"),
    sortKey: "appointment",
    width: 150,
    cardRole: "meta",
    cell: (report) => (
      <span className="text-[13px] tabular-nums text-slate-500">
        {fmtDate(reportAppointmentDate(report))}
      </span>
    ),
  },
  {
    key: "customer",
    label: t("Customer / Project"),
    sortKey: "customer",
    width: 220,
    groupable: true,
    cardRole: "detail",
    groupValue: (report) => report?.customer?.name || "",
    groupLabel: (key) => key || t("table_view.no_value"),
    cell: (report) => (
      <div className="flex min-w-0 flex-col gap-0.5">
        <CellLink
          to={report?.customer?.id ? `/dashboard/customers/${report.customer.id}` : null}
          className="truncate text-[13.5px] font-medium text-slate-800"
        >
          {report?.customer
            ? `${report.customer.customer_number || ""} ${report.customer.name || ""}`.trim()
            : "—"}
        </CellLink>
        {report?.project?.id && (
          <span className="truncate text-[12px] text-slate-400">
            {report.project.displayTitle || report.project.name || ""}
          </span>
        )}
      </div>
    ),
  },
  {
    key: "address",
    label: t("Address"),
    width: 200,
    cardRole: "detail",
    cell: (report) => (
      <div className="max-w-[200px] truncate text-[13px] text-slate-500">
        {FullReportAddress(report) || "—"}
      </div>
    ),
  },
  {
    key: "created_at",
    label: t("Created"),
    sortKey: "created_at",
    width: 140,
    defaultVisible: false,
    cardRole: "detail",
    cell: (report) => (
      <span className="text-[13px] tabular-nums text-slate-500">{fmtDate(report.created_at)}</span>
    ),
  },
];

export const ReportsTable = ({
  itemState = { data: [], isLoading: false },
  columns,
  visibleKeys,
  widths,
  onResize,
  groupBy = "none",
  sort = null,
  onSort,
  density = "comfortable",
  hasMore = false,
  onLoadMore,
  onItemsSelect = () => {},
  onRowClick,
  layout,
  hideSelection = false,
}) => {
  const { data = [], isLoading } = itemState;
  return (
    <PMDataTable
      columns={columns}
      visibleKeys={visibleKeys}
      widths={widths}
      onResize={onResize}
      groupBy={groupBy}
      sort={sort}
      onSort={onSort}
      data={data}
      isLoading={isLoading}
      density={density}
      hasMore={hasMore}
      onLoadMore={onLoadMore}
      onSelectionChange={onItemsSelect}
      onRowClick={onRowClick}
      mobileRowLayout
      layout={layout}
      hideSelection={hideSelection}
    />
  );
};

ReportsTable.defaultProps = {
  itemState: { data: [], isLoading: false },
  density: "comfortable",
  hasMore: false,
};

export default ReportsTable;
